"use strict";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import {
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Input,
  Select,
  Tabs,
  rem
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBuildingSkyscraper,
  IconChartDonut3,
  IconFileExport,
  IconSearch,
  IconTable,
  IconTimeDuration15
} from "@tabler/icons-react";
import {
  ColDef,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import {
  paginationPageSizeSelector,
  repositoryMdm,
  repositoryPos
} from "../../../_base/_const/_constVar";
import BreadcrumbLayout1 from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { MessageResponse } from "../../../model/MessageResponse";

import { AgChartOptions } from "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";
import { BASE_API_MDM } from "../../../config";
// Đăng ký module Infinite Row Model
ModuleRegistry.registerModules([InfiniteRowModelModule]);

export interface IStorageAgePieChartItem {
  mA_CHI_NHANH: string;
  nhoM_THOI_GIAN: string;
  giA_TRI_TON: number;
  sO_LUONG_SP: number;
  tY_TRONG_PHAN_TRAM: number;
  sorT_ORDER: number;
  thoI_GIAN_ORDER: number;
}

const GetWeightSerial = () => {
  // Không cần state rowData hay loading thủ công nữa, AG Grid tự quản lý
  const gridRef = useRef<AgGridReact<any>>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(750);
  const [dataBranchCode, setDataBranchCode] = useState<any[]>([]);

  const containerStyle = useMemo(
    () => ({ width: "100%", height: height }),
    [height],
  );
  const gridStyle = useMemo(() => ({ height: `${height + 80}px `, width: "100%" }), [height]);
  const sizeGrid = { base: 12, sm: 6, md: 6, lg: 2.5 };
  const [visible, { toggle, close, open }] = useDisclosure(false);

  const [chartData, setChartData] = useState<any[]>([]);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState<any[]>([]);

  // Form Filter
  const form = useForm<{
    KeySearch: string;
    MaChiNhanh: string | null;
    TuNgay: string | null;
    DenNgay: string | null;
    Tabs: string;
  }>({
    initialValues: {
      KeySearch: "",
      MaChiNhanh: "",
      TuNgay: dayjs().subtract(1, "month").format("YYYY-MM-DD"),
      DenNgay: dayjs().format("YYYY-MM-DD"),
      Tabs: "table",
    },
  });

  // Cho hình tròn
  const ChartPieRef = useRef<HTMLDivElement>(null);

  // Xử lý dữ liệu biểu đồ tách riêng để đảm bảo reactivity
  const processedChartData = useMemo(() => {
    return chartData.filter((item: any) => item.mA_CHI_NHANH === "TOTAL");
  }, [chartData]);

  // Chart Pie Options
  const pieOptions = useMemo<AgChartOptions>(() => {
    return {
      autoSize: true,
      theme: "ag-default",
      title: {
        text: `Báo cáo tỷ trọng hàng tồn kho theo thời gian`,
        fontSize: 22,
        color: '#000',
      },
      data: processedChartData,
      series: [
        {
          type: 'pie',
          angleKey: 'giA_TRI_TON',
          legendItemKey: 'nhoM_THOI_GIAN',
          calloutLabelKey: 'nhoM_THOI_GIAN',
          sectorLabelKey: 'giA_TRI_TON',
          sectorLabel: {
            enabled: true,
            color: '#fff',
            fontWeight: 'bold',
            formatter: ({ datum }: any) => Number(datum.giA_TRI_TON).toLocaleString('vi-VN'),
          },
          calloutLabel: {
            enabled: true,
            color: '#000',
            fontSize: 12,
            offset: 10,
          },
          calloutLine: {
            strokeWidth: 1,
            colors: ['#000'],
          },
          tooltip: {
            renderer: ({ datum }: any) =>
              `${datum.nhoM_THOI_GIAN} — ${Number(datum.giA_TRI_TON).toLocaleString('vi-VN')}`
          },
        },
      ],
      highlightStyle: {
        item: {
          strokeWidth: 5,
        },
        series: {
          opacity: 0.2,
        },
      },
      legend: {
        position: 'bottom',
        item: {
          label: {
            color: '#000',
          },
          paddingX: 20
        },
      },
      padding: { top: 20, right: 20, bottom: 20, left: 20 },
    } as AgChartOptions;
  }, [processedChartData]);


  // Auto resize height
  useEffect(() => {
    const updateHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const newHeight = window.innerHeight - (210 + headerHeight);
      setHeight(newHeight);
    };
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // --- LOGIC GỌI API (Được gọi bởi Datasource) ---
  const fetchData = async (startRow: number, endRow: number) => {
    try {
      const skip = startRow;
      const take = endRow - startRow;

      let url = `/api/v1/BaoCaoTuoiTonController_PieChart_/get-list?Skip=${skip}&Take=${take}`;
      const values = form.getValues();

      // if (values.MaChiNhanh && values.MaChiNhanh.length > 0) {
      //   values.MaChiNhanh.forEach((branchCode: string) => {
      //     url += `&MaChiNhanh=${branchCode}`;
      //   });
      // }
      if (values.MaChiNhanh) url += `&MaChiNhanh=${values.MaChiNhanh}`;
      // if (values.TuNgay) url += `&TuNgay=${values.TuNgay}`;
      // if (values.DenNgay) url += `&DenNgay=${values.DenNgay}`;
      if (values.KeySearch) url += `&KeySearch=${values.KeySearch.trim()}`;

      const response = await repositoryMdm.get<MessageResponse<IStorageAgePieChartItem[]>>(url);
      return response;
    } catch (error) {
      console.error("API Error:", error);
      return null;
    }
  };

  // --- TẠO DATASOURCE CHO INFINITE ROW MODEL ---
  const getDatasource = useCallback((): IDatasource => {
    return {
      getRows: async (params: IGetRowsParams) => {
        const { startRow, endRow } = params;
        gridRef.current?.api?.showLoadingOverlay();
        try {
          const response = await fetchData(startRow, endRow);

          if (response && response.success) {
            const data = response.data || [];
            const totalCount = (response as any).totalCount ?? 0;

            // Tách các dòng TOTAL để ghim xuống dưới
            const totalRows = data.filter((item: any) => item.mA_CHI_NHANH === "TOTAL");
            setPinnedBottomRowData(totalRows);

            // Chỉ hiển thị các dòng không phải TOTAL trong bảng chính
            const mainData = data.filter((item: any) => item.mA_CHI_NHANH !== "TOTAL");

            setChartData(data);

            gridRef.current?.api?.hideOverlay();
            params.successCallback(mainData, totalCount);
          } else {
            params.failCallback();
            gridRef.current?.api?.hideOverlay();
          }
        } catch (e) {
          params.failCallback();
          gridRef.current?.api?.hideOverlay();
        }
      },
    };
  }, [dataBranchCode]);


  // Tự động cập nhật datasource khi danh mục chi nhánh nạp xong
  useEffect(() => {
    if (gridRef.current && gridRef.current.api && dataBranchCode.length > 0) {
      gridRef.current.api.setGridOption("datasource", getDatasource());
    }
  }, [getDatasource, dataBranchCode]);

  // --- SỰ KIỆN BẤM TÌM KIẾM ---
  const handleSearch = useCallback(() => {
    form.setErrors({});
    const values = form.getValues();
    if (
      values.TuNgay &&
      values.DenNgay &&
      dayjs(values.TuNgay).isAfter(dayjs(values.DenNgay))
    ) {
      form.setFieldError(
        "TuNgay",
        "Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc",
      );
      return;
    }

    if (gridRef.current && gridRef.current.api) {
      // Set lại datasource mới sẽ kích hoạt Grid reset về trang 1 và gọi lại getRows với filter mới
      gridRef.current.api.setGridOption("datasource", getDatasource());
    }
  }, [form, getDatasource]);

  // --- ON GRID READY ---
  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      // Gán datasource ban đầu
      params.api.setGridOption("datasource", getDatasource());
    },
    [getDatasource],
  );

  // --- CÁC HÀM TIỆN ÍCH KHÁC GIỮ NGUYÊN ---
  const getRowId = useCallback((params: any) => {
    const d = params.data || {};
    return (
      `${d.mA_CHI_NHANH}_${d.nhoM_THOI_GIAN}_${d.tY_TRONG_PHAN_TRAM}_${d.giA_TRI_TON}_${d.sO_LUONG_SP}`.replace(/(^_+|_+$)/g, "") ||
      Math.random().toString()
    );
  }, []);

  const getRowStyle = useCallback((params: any) => {
    if (params.node.rowPinned) {
      return {
        backgroundColor: "#e7f5ff",
        fontWeight: 700,
        fontSize: "14px",
        borderTop: "3px solid #15aabf",
      };
    }
    return undefined;
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { field: "mA_CHI_NHANH", headerName: "Mã chi nhánh", width: 200, },
      { headerName: "Tên chi nhánh", width: 200, cellRenderer: (params: any) => params.data ? dataBranchCode?.find((i: any) => Number(i.value) === Number(params.data.mA_CHI_NHANH))?.label : "" },
      { field: "nhoM_THOI_GIAN", headerName: "Nhóm thời gian", minWidth: 300 },
      {
        field: "giA_TRI_TON",
        headerName: "Giá trị tồn",
        width: 200,
        valueFormatter: (p: any) => (p.value != null ? Number(p.value).toLocaleString("vi-VN") : "0"),
      },
      {
        field: "sO_LUONG_SP",
        headerName: "Số lượng SP",
        width: 200,
        valueFormatter: (p: any) => (p.value != null ? Number(p.value).toLocaleString("vi-VN") : "0"),
      },
      {
        field: "tY_TRONG_PHAN_TRAM",
        headerName: "Tỷ trọng (%)",
        width: 200,
        valueFormatter: (p: any) => (p.value != null ? `${p.value}%` : "0%"),
      },
    ],
    [dataBranchCode],
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: false, // Infinite model server-side sort hơi phức tạp, tạm tắt hoặc cần handle trong getRows
      resizable: true,
    };
  }, []);


  const DataBrachCode = async () => {
    /* ... Logic cũ ... */
    try {
      const response = await repositoryPos.get(
        `api/v1/TblDmInventory/get-select-branch?auth=true`,
      );
      if (response && response.success) {
        const transformedData = response.data
          .filter((i: any) => i.att2 && i.text)
          .map((item: any) => {
            return {
              value: item.att2,
              label: item.att2 + " - " + item.text,
              id: item.value,
            };
          });
        setDataBranchCode(transformedData);
      }
    } catch (error) {
      console.error(error);
    }
  };



  useEffect(() => {
    DataBrachCode();
  }, []);
  //#region Download Image
  const handleDownloadImage = async (refCurrent: React.RefObject<HTMLDivElement>) => {
    const html2canvas = await import("html2canvas-pro");

    if (!refCurrent.current) {
      console.error("Error: refCurrent is null.");
      return;
    }

    // setIsLoading(true);
    open(); // Hiển thị loading

    try {
      // 🚀 TRICK 1: Pre-optimize DOM trước khi capture
      const element = refCurrent.current;

      // Set fixed size để html2canvas xử lý nhanh hơn
      const originalStyle = element.style.cssText;
      element.style.width = `${element.scrollWidth}px`;
      element.style.transform = "scale(1)";
      element.style.transformOrigin = "top left";

      // 🚀 TRICK 2: Hide unnecessary elements
      const unnecessaryElements = element.querySelectorAll(
        "button, .fa-solid, .fa-regular, svg",
      );
      const hiddenElements: HTMLElement[] = [];
      unnecessaryElements.forEach((el: any) => {
        if (el.style.display !== "none") {
          hiddenElements.push(el);
          el.style.display = "none";
        }
      });

      // 🚀 TRICK 3: Html2canvas với settings tối ưu tối đa
      const canvas = await html2canvas.default(element, {
        scale: 1, // 🔥 Key optimization: scale = 1
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        width: element.scrollWidth,
        logging: false,
        imageTimeout: 1000, // 🔥 Giảm timeout
        removeContainer: false,
        foreignObjectRendering: false,
        ignoreElements: (element) => {
          // 🔥 Skip heavy elements
          return (
            element.tagName === "BUTTON" ||
            element.tagName === "SVG" ||
            element.classList.contains("fa-solid") ||
            element.classList.contains("fa-regular")
          );
        },
      });

      // 🚀 TRICK 4: Restore DOM ngay lập tức
      element.style.cssText = originalStyle;
      hiddenElements.forEach((el) => {
        el.style.display = "";
      });

      // 🚀 TRICK 5: Generate filename nhanh
      const now = new Date();
      const fileName = `Bieu-Do-Bao-Cao-Ton-Kho-Theo-Chi-Nhanh-Qua-Khu-${form.getValues().Tabs === "chartColumn" ? "hinh-Cot" : "hinh-Tron"}-${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}`;

      // 🚀 TRICK 6: Download trực tiếp bằng toBlob (nhanh nhất)
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${fileName}.jpg`;
            link.click();
            URL.revokeObjectURL(url);
            NotificationExtension.Success("Tải ảnh thành công!");
          }
        },
        "image/jpeg",
        0.85,
      );
    } catch (error) {
      console.error("Error generating image:", error);
      NotificationExtension.Fails("Tải ảnh thất bại!");
    } finally {
      // setIsLoading(false);
      close(); // Đóng loading
    }
  };
  // Export Excel
  const exportDataExcel = async () => {
    open();
    try {
      let url = "";

      const values = form.getValues();

      if (values.MaChiNhanh) url += `&MaChiNhanh=${values.MaChiNhanh}`;
      // if (values.TuNgay) url += `&TuNgay=${values.TuNgay}`;
      // if (values.DenNgay) url += `&DenNgay=${values.DenNgay}`;
      if (values.KeySearch) url += `&KeySearch=${values.KeySearch.trim()}`;


      const baseUrl = `/api/v1/BaoCaoTuoiTonController_PieChart_/export-bao-cao-tuoi-ton${url.startsWith("&") ? "?" + url.substring(1) : url}`;

      const _url = BASE_API_MDM + baseUrl;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
      } else {
        const blob = await response.blob();
        const urlss = window.URL.createObjectURL(blob);
        const _name = new Date().toISOString().slice(0, 10);
        let filename = "danh_sach_voucher.xlsx";
        const link = document.createElement("a");
        link.href = urlss;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        NotificationExtension.Success("Export Excel thành công !");
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  return (
    <Box pos={"relative"} style={containerStyle}>
      <LoadingOverlayCustom visible={visible} />
      <Tabs value={form.getValues().Tabs} onChange={(value) => {
        form.setFieldValue("Tabs", value || "PieChart");
        // reload data theo loại mới
        gridRef.current?.api?.refreshServerSide?.({ purge: true });
      }} >
        <div
          style={{
            border: "1px solid #dee2e6",
            padding: "10px",
            overflowX: "hidden",
          }}
        >
          <Group justify="space-between" gap={0}>
            <BreadcrumbLayout1 />
            <Button
              leftSection={
                <IconFileExport style={{ width: rem(14), height: rem(14) }} />
              }
              onClick={exportDataExcel}
            >
              Export Excel
            </Button>
          </Group>
          <Flex justify={"space-between"} style={{ marginBottom: 12 }}>
            <Grid mt={10}>
              {/* GIỮ NGUYÊN UI FILTER NHƯ CŨ */}
              <Grid.Col span={sizeGrid}>
                <Input
                  placeholder="Nhập tìm kiếm"
                  leftSection={<IconSearch size={20} color="#15aabf" />}
                  value={form.getValues().KeySearch}
                  onChange={(e) => form.setFieldValue("KeySearch", e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
              </Grid.Col>
              <Grid.Col span={sizeGrid}>
                <Select
                  placeholder="Chi nhánh"
                  clearable
                  searchable
                  data={dataBranchCode}
                  value={form.getValues().MaChiNhanh}
                  onChange={(v) => {
                    form.setFieldValue("MaChiNhanh", v);
                  }}
                  leftSection={<IconBuildingSkyscraper size={20} color="#15aabf" />}
                />
              </Grid.Col>
              {/* <Grid.Col span={sizeGrid}>
                <DatePickerInput
                  size="sm"
                  placeholder="Chọn ngày bắt đầu"
                  locale="vi"
                  valueFormat="DD/MM/YYYY"
                  leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
                  {...form.getInputProps("TuNgay")}
                  value={
                    form.getValues().TuNgay
                      ? dayjs(form.getValues().TuNgay, "YYYY-MM-DD").toDate()
                      : null
                  }
                  onChange={(date) =>
                    form.setFieldValue(
                      "TuNgay",
                      date ? dayjs(date).format("YYYY-MM-DD") : null,
                    )
                  }
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={sizeGrid}>
                <DatePickerInput
                  size="sm"
                  placeholder="Chọn ngày kết thúc"
                  locale="vi"
                  valueFormat="DD/MM/YYYY"
                  leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
                  {...form.getInputProps("DenNgay")}
                  value={
                    form.getValues().DenNgay
                      ? dayjs(form.getValues().DenNgay, "YYYY-MM-DD").toDate()
                      : null
                  }
                  onChange={(date) =>
                    form.setFieldValue(
                      "DenNgay",
                      date ? dayjs(date).format("YYYY-MM-DD") : null,
                    )
                  }
                  clearable
                />
              </Grid.Col> */}
              <Grid.Col span={{ base: 12, sm: 6, md: 6, lg: 0.9 }}>
                <Button color="blue" variant="outline" onClick={handleSearch}>
                  Tìm kiếm
                </Button>
              </Grid.Col>
            </Grid>
            <Group>
              <Tabs.List>
                {/* Biểu đồ hình tròn */}
                <Tabs.Tab value="PieChart" >
                  <IconChartDonut3 size={24} />
                </Tabs.Tab>
                {/* table bình thường */}
                <Tabs.Tab value="table" >
                  <IconTable size={24} />
                </Tabs.Tab>
              </Tabs.List>
            </Group>
          </Flex>
        </div>
        <Tabs.Panel value="table" style={{ height: `${height}px !important` }}>          <div style={gridStyle} className={"ag-theme-quartz"}>
          <AgGridReact<IStorageAgePieChartItem>
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            // --- CẤU HÌNH QUAN TRỌNG CHO INFINITE ROW MODEL ---
            rowModelType={"infinite"} // Chế độ load theo trang từ server
            // Kích thước mỗi lần gọi API (Take = 100)
            cacheBlockSize={100}
            // Số dòng tối đa giữ trong cache trình duyệt (tùy chỉnh để tối ưu RAM)
            maxBlocksInCache={10}
            // Hiển thị phân trang
            pagination={true}
            paginationPageSize={100} // Phải khớp hoặc là bội số của cacheBlockSize
            paginationPageSizeSelector={paginationPageSizeSelector} // [100, 200, 500]
            onGridReady={onGridReady}
            getRowId={getRowId}
            getRowStyle={getRowStyle}
            pinnedBottomRowData={pinnedBottomRowData}
            localeText={AG_GRID_LOCALE_VN}
          />
        </div>
        </Tabs.Panel>


        <Tabs.Panel value="PieChart" pt="xs" style={{ height: `${height + 700}px` }}>
          <Group justify="end" mt="md">
            <Button color="teal" onClick={() => handleDownloadImage(ChartPieRef)}>
              Tải ảnh biểu đồ tròn
            </Button>
          </Group>
          <div ref={ChartPieRef}
            style={{
              width: '100%',
            }}>
            {chartData && chartData.length > 0 ? (
              <AgCharts
                options={pieOptions}
                style={{ width: "100%", height: "750px" }}
              />
            ) : (
              <Box
                style={{
                  width: "100%",
                  height: "750px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px dashed #e9ecef",
                  color: "#666",
                }}
              >
                Không có dữ liệu
              </Box>
            )}
          </div>
        </Tabs.Panel>

      </Tabs>
    </Box>
  );
};
export default GetWeightSerial;
